import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { AppShell } from "@/components/library/layout";
import { SearchBar, GlobalSearch } from "@/components/library/search";
import { NotificationCenter } from "@/components/library/collab";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { QuickActionButton } from "@/components/workspace/QuickActionButton";
import { FloorBar } from "@/components/workspace/FloorBar";
import { AssistantWidget } from "@/components/workspace/AssistantWidget";
import { ensureSeed, useStore, list, search, markNotificationRead, notificationsFor } from "@/lib/dataStore";

export default function WorkspaceShell() {
  useStore();
  const { currentRestaurant, currentOrganization, profile, role, roleLabel, signOut, hasPermission, currentMembership } = useSbAuth();
  const rid = currentRestaurant?.id;
  const nav = useNavigate();
  const loc = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { if (rid) ensureSeed(rid); }, [rid]);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);

  const groups = useMemo(() => (rid ? search(rid, q) : {}), [rid, q]);
  // Staff/shift-leaders (assigned a department) only see their department's notifications; managers/owners see all.
  const scopeDept = currentMembership?.department || null;
  const notifs = rid ? notificationsFor(rid, scopeDept).map((n) => ({ ...n })) : [];

  const navItems = [
    { key: "", label: "Hem", icon: "LayoutDashboard" },
    { key: "live", label: "Live-vy", icon: "MonitorPlay" },
    { key: "operation", label: "Operation", icon: "LayoutGrid" },
  ];
  if (hasPermission("user.read")) navItems.push({ key: "roller", label: "Roller", icon: "Users" });
  if (hasPermission("dashboard.access")) navItems.push({ key: "ledning", label: "Ledning", icon: "PieChart" });
  if (hasPermission("foodcost.access")) navItems.push({ key: "foodcost", label: "Ekonomi", icon: "BarChart3" });
  navItems.push({ key: "profil", label: "Profil", icon: "Users" });

  const seg = loc.pathname.replace(/^\/konto\/?/, "").split("/")[0];
  const activeKey = navItems.some((n) => n.key === seg) ? seg : "";

  const TYPE_TAB = { checklist: "checklistor", task: "uppgifter", incident: "problem", manual: "manualer" };
  const goSearch = (type, r) => {
    setSearchOpen(false);
    const dept = r.department || "kunskap";
    nav(`/konto/operation/${dept}`, { state: { tab: TYPE_TAB[type] || "oversikt", openId: r.ref_id, openType: type } });
  };

  return (
    <>
      <AppShell
        brand="Restaurangkollen"
        navItems={navItems}
        activeKey={activeKey}
        onNavigate={(k) => nav(k ? `/konto/${k}` : "/konto")}
        user={{ name: profile?.full_name, role, title: roleLabel, avatar: profile?.avatar_url }}
        topbarRight={
          <div className="flex items-center gap-2">
            <SearchBar onClick={() => setSearchOpen(true)} />
            <ThemeToggle />
            <NotificationCenter notifications={notifs} onRead={(id) => markNotificationRead(rid, id)}
              onSelect={(n) => { if (n.link) nav(n.link, n.tab ? { state: { tab: n.tab } } : undefined); }} />
            <button onClick={async () => { await signOut(); nav("/konto/login"); }} data-testid="ws-signout"
              className="grid place-items-center h-9 w-9 rounded-xl bg-secondary/60 border border-border text-muted-foreground hover:text-foreground transition-colors" title="Logga ut">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        }
      >
        <div className="pb-20 md:pb-0">
          <Outlet />
        </div>
      </AppShell>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} query={q} onQuery={setQ} groups={groups} onSelect={goSearch} />
      <QuickActionButton />
      <FloorBar />
      <AssistantWidget />
    </>
  );
}

